import {
    ValueMetadata,
    NameValue,
    AdrValue,
    GeoValue,
    ImppValue,
    ClientpidmapValue,
    GenderValue
} from '../../../types/ParsedVCardTypes';
import {
    TYPE,
    FN,
    N,
    ORG,
    ADR,
    NICKNAME,
    BDAY,
    ANNIVERSARY,
    LANGUAGE,
    GENDER,
    GEO,
    PHOTO,
    LOGO,
    IMPP,
    URI,
    CLIENTPIDMAP,
    CATEGORIES,
    TZ,
    TITLE,
    ROLE,
    MEMBER,
    RELATED
} from '../constants-vcard4';
import { parseVCardDate } from './helper';

type Metadata = { [key: string]: any };

/**
 * Safely decode base64 or return URL if it's already a URL
 * @param value - The value to decode (either URL or base64)
 * @returns The decoded value, original URL, or empty string on error
 */
function safeDecodeBase64OrUrl(value: string): string {
    const urlRegex = /^\w+:\/\//; // very basic scheme check to validate URL

    // If it's already a URL, return as-is
    if (urlRegex.test(value)) {
        return value;
    }

    // Try to decode base64
    try {
        return atob(value);
    } catch (e) {
        console.warn('Failed to decode base64, using empty string:', e);
        return ''; // Use empty string for invalid base64
    }
}

function parseNameField(familyName: string, givenName: string, additionalNames: string, prefixes: string, suffixes: string, metadata: Metadata) : ValueMetadata<NameValue> {
    return {
        value: {
            familyName: familyName.split(','),
            givenName: givenName.split(','),
            additionalNames: additionalNames.split(','),
            prefixes: prefixes.split(','),
            suffixes: suffixes.split(',')
        },
        ...metadata
    };
}

function parseAdrField(postOfficeBox: string, extendedAddress: string, street: string, locality: string, region: string, postalCode: string, country: string, metadata: Metadata) : ValueMetadata<AdrValue> {
    return {
        value: {
            postOfficeBox: postOfficeBox.split(','),
            extendedAddress: extendedAddress.split(','),
            street: street.split(','),
            locality: locality.split(','),
            region: region.split(','),
            postalCode: postalCode.split(','),
            country: country.split(',')
        },
        ...metadata
    };
}

export const propertyParsers = {
    [FN]: (value: string, metadata: Metadata) : ValueMetadata<string> => propertyParsers["default"](value, metadata),
    [N]: (value: string, metadata: Metadata) : ValueMetadata<NameValue> => {
        const [familyName, givenName, additionalNames, prefixes, suffixes] = value.split(';');
        return parseNameField(familyName, givenName, additionalNames, prefixes, suffixes, metadata);
    },
    [ORG]: (value: string, metadata: Metadata) : ValueMetadata<string[]> =>{
        return {
            value: value.split(';'),
            ...metadata
        };
    },
    [ADR]: (value: string, metadata: Metadata) : ValueMetadata<AdrValue> => {
        let [postOfficeBox, extendedAddress, street, locality, region, postalCode, country] = value.split(';');
        return parseAdrField(postOfficeBox, extendedAddress, street, locality, region, postalCode, country, metadata);
    },
    [NICKNAME]: (value: string, metadata: Metadata) : ValueMetadata<string[]> => {
        return {
            value: value.split(','),
            ...metadata
        };
    },
    [BDAY]: parseVCardDate,
    [ANNIVERSARY]: parseVCardDate,
    [LANGUAGE]: (value: string, metadata: Metadata) : ValueMetadata<string[]> => {
        return {
            value: value.split(','),
            ...metadata
        };
    },
    [GEO]: (value: string, metadata: Metadata) : ValueMetadata<GeoValue> => {
        const [latitude, longitude] = value.split(";");
        return {
            value: {
                latitude: parseFloat(latitude),
                longitude: parseFloat(longitude)
            },
            ...metadata
        };
    },
    [PHOTO]: (value: string, metadata: Metadata) : ValueMetadata<string> => {
        return {
            value: safeDecodeBase64OrUrl(value),
            ...metadata
        };
    },
    [LOGO]: (value: string, metadata: Metadata) : ValueMetadata<string> => {
        return {
            value: safeDecodeBase64OrUrl(value),
            ...metadata
        };
    },
    [IMPP]: (value: string, metadata: Metadata) : ValueMetadata<ImppValue> => {
        const [protocol, imppAddress] = value.split(":");
        return {
            value: {
                protocol: protocol,
                address: imppAddress
            },
            ...metadata
        };
    },
    [CLIENTPIDMAP]: (value: string, metadata: Metadata) : ValueMetadata<ClientpidmapValue> => {
        const [pid, uri] = value.split(";");
        return {
            value: {
                pid: pid,
                uri: uri
            },
            ...metadata
        };
    },
    [CATEGORIES]: (value: string, metadata: Metadata) : ValueMetadata<string[]> => {
        return {
            value: value.split(','),
            ...metadata
        };
    },
    [GENDER]: (value: string, metadata: Metadata) : ValueMetadata<GenderValue> => {
        const [sex, genderIdentity] = value.split(";");
        return {
            value: {
                sex: sex || "",
                genderIdentity: genderIdentity || ""
            },
            ...metadata
        };
    },

    [URI]: (value: string, metadata: Metadata) : ValueMetadata<string> => propertyParsers["default"](value, metadata),
    [TZ]: (value: string, metadata: Metadata) : ValueMetadata<string> => propertyParsers["default"](value, metadata),
    [TITLE]: (value: string, metadata: Metadata) : ValueMetadata<string> => propertyParsers["default"](value, metadata),
    [ROLE]: (value: string, metadata: Metadata) : ValueMetadata<string> => propertyParsers["default"](value, metadata),
    [MEMBER]: (value: string, metadata: Metadata) : ValueMetadata<string> => propertyParsers["default"](value, metadata),
    [RELATED]: (value: string, metadata: Metadata) : ValueMetadata<string> => propertyParsers["default"](value, metadata),
    // For properties that aren't specifically handled, we'll use a default function that just sets the value and metadata.
    'default': (value: string, metadata: Metadata) : ValueMetadata<string> => {
        return {
            value: value,
            ...metadata
        };
    }
}

export function parseParameters(parts: string[]) {
    const params = parts.reduce((acc: any, item: string) => {
        if (item.includes('=')) {
            const [attrName, attrValue] = item.split('=');
            const attrLower = attrName.toLowerCase();
            if (attrValue) {
                acc[attrLower] = attrValue.includes(',') ? attrValue.split(',') : attrValue;
            }
        } else if (item.toLowerCase() === TYPE.WORK || item.toLowerCase() === TYPE.HOME) {
            // Default to "type" if no explicit type is given
            if (acc.type === undefined) {
                acc.type = [];
            }
            acc.type.push(item.toLowerCase());
        }
        return acc;
    }, {});

    // Handle Language parameter (defined by the IETF's BCP 47) separately
    // positive integer to represent preference of language (the default value is 1)
    if (params.language) {
        params.language = params.language.toLowerCase();
    } else {
        params.language = 1;
    }

    return params;
}